import {
  Features,
  useIsFeatureToggleEnabled,
} from '@backoffice-frontend/feature-toggle'
import { useMetricTracking } from '@backoffice-frontend/tracking'
import {
  DeviceLabels,
  useMeetingManager,
} from 'amazon-chime-sdk-component-library-react'
import { MeetingSessionConfiguration } from 'amazon-chime-sdk-js'
import { useCallback, useEffect, useRef, useState } from 'react'
import { LoadingLivestream } from '../components/LoadingLivestream'
import { VideoWall } from './VideoWall'
import { VideoWallCockpitView } from './VideoWallCockpitView'

export const ChimeMeeting = ({
  credentials,
  vehicleId,
}: {
  credentials: string
  vehicleId: string
}) => {
  const isCockpitViewEnabled = useIsFeatureToggleEnabled(
    Features.COCKPIT_CAMERA_VIEW,
  )
  const meetingManager = useMeetingManager()
  const [loading, setLoading] = useState(true)
  const { trackMetric } = useMetricTracking()
  const joinedAttendees = useRef(new Set<string>())

  const fetchConfig = useCallback(async () => {
    const joinStartTimestamp = Date.now()
    const joinInfo = JSON.parse(credentials)
    const { Meeting: meeting, Attendee: attendee } = joinInfo
    const meetingSessionConfiguration = new MeetingSessionConfiguration(
      { Meeting: meeting },
      { Attendee: attendee },
    )
    const { meetingId } = meetingSessionConfiguration
    if (!meetingId) {
      return
    }

    await meetingManager.join(meetingSessionConfiguration, {
      // We are just observing the video, no need to request permissions
      deviceLabels: DeviceLabels.None,
    })
    meetingManager.meetingSession?.audioVideo.addObserver({
      metricsDidReceive: report => {
        const rtcStatsReport = report.getRTCStatsReport()

        rtcStatsReport.forEach(stats => {
          if (
            stats.type === 'candidate-pair' &&
            stats.nominated &&
            'currentRoundTripTime' in stats
          ) {
            const rttMs = stats.currentRoundTripTime * 1000
            void trackMetric({
              metricName: 'NetworkRoundTripTime',
              value: rttMs,
              dimensions: [
                { name: 'meetingId', value: meetingId },
                { name: 'vehicleId', value: vehicleId },
              ],
              namespace: 'FleetControl/ad-operator-video',
            })
          }
          if (stats.type === 'inbound-rtp' && stats.kind === 'video') {
            if ('jitter' in stats) {
              const jitterMs = stats.jitter * 1000
              void trackMetric({
                metricName: 'VideoJitter',
                value: jitterMs,
                dimensions: [
                  { name: 'meetingId', value: meetingId },
                  { name: 'vehicleId', value: vehicleId },
                ],
                namespace: 'FleetControl/ad-operator-video',
              })
            }
          }
        })
      },
      remoteVideoSourcesDidChange(videoSources) {
        videoSources.forEach(async videoSource => {
          const attendeeId = videoSource.attendee.externalUserId
          if (attendeeId && !joinedAttendees.current.has(attendeeId)) {
            joinedAttendees.current.add(attendeeId)

            const joinDuration = Date.now() - joinStartTimestamp
            await trackMetric({
              metricName: 'AttendeeJoinTime',
              value: joinDuration,
              dimensions: [
                { name: 'attendeeId', value: attendeeId },
                { name: 'meetingId', value: meetingId },
                { name: 'vehicleId', value: vehicleId },
              ],
              namespace: 'FleetControl/ad-operator-video',
            })
          }
        })
      },
      audioVideoDidStart: async () => {
        setLoading(false)
        await trackMetric({
          metricName: 'ChimeLoadingCompleted',
          value: 1,
          dimensions: [
            { name: 'meetingId', value: meetingId },
            {
              name: 'joinDurationMs',
              value: (Date.now() - joinStartTimestamp).toString(),
            },
          ],
          namespace: 'FleetControl/ad-operator-video',
        })
      },
    })
    meetingManager.meetingSession?.audioVideo.start()
  }, [meetingManager, credentials, vehicleId, trackMetric])

  useEffect(() => {
    fetchConfig()
  }, [fetchConfig])

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to leave the meeting at all cost when the component unmounts, but not when the meetingManager updates
  useEffect(() => {
    return () => {
      void meetingManager.leave()
    }
  }, [])

  if (loading) {
    return <LoadingLivestream />
  } else {
    return isCockpitViewEnabled ? (
      <VideoWallCockpitView vehicleId={vehicleId} />
    ) : (
      <VideoWall vehicleId={vehicleId} />
    )
  }
}
